import React, { useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";

/**
 * PC/VR Games 탭의 서브내비 (#5407 · #5416, 기획 #5363 §범위).
 *
 * ⚠️ 두 번째 탭 라벨은 **`PC Games`** / **`VR Games`** 다. #5363 본문 §범위는 화면을
 * 「Overview / 지원 게임 / Software」로 부르지만 그건 화면 이름이고, 시안
 * (`29499:63547` · `29503:242104`)과 프로토타입 모두 탭 라벨은 플랫폼 이름이다.
 *
 * 목적지는 푸터 v2(#5350)가 이미 쓰는 것과 같다 — 세 곳이 갈라지지 않게 여기서도 같은 URL 을 쓴다.
 *
 * 기존 `GameTabMenu`(9탭 가로 스크롤, 플랫폼별)와는 **다른 물건**이다. 그건 게임 목록의
 * 플랫폼 필터고 이건 탭 안의 화면 전환이다.
 *
 * ## 왜 프롭이 아니라 경로로 고르나
 *
 * 이 컴포넌트는 active 판정에 **이미 `router.pathname` 을 읽는다.** 같은 값으로 목록까지
 * 고르면 호출처가 `<GamesSubNav />` 하나로 유지되고, 새 탭이 생겨도 호출처를 안 고친다.
 * 프롭으로 받으면 호출처 4곳이 각자 자기 플랫폼을 다시 적어야 하고, 그 둘이 어긋나면
 * (`/games/vr` 에서 PC 목록) 조용히 틀린다 — 경로가 유일한 출처면 어긋날 자리가 없다.
 */
/**
 * ★**가운데 탭은 `Supported {PC,VR} Games` 다** (2026-09-07 요청).
 *
 * 종전엔 서브내비가 `PC Games` / `VR Games` 이고 GNB 패널만 `All supported games` 로
 * **라벨을 덮고** 있었다(「화면 안에서는 PC 게임, 메뉴에서는 전체를 강조」가 근거였다).
 * 이제 **둘을 같은 문구로** 맞추므로 그 덮어쓰기를 걷었다 —
 * `navigation-menu-content.tsx` · `menu-view.tsx` 가 `tab.label` 을 그대로 쓴다.
 *
 * ⚠️ **GNB 1차 메뉴는 그대로 `PC Games` / `VR Games`** 다(`navigation-menus.ts` 의
 * `MENU_NAME`). 바뀌는 것은 그 아래 3항목 중 **가운데 하나**뿐이다.
 */
/**
 * 「지원 게임 전체」 탭의 목적지.
 *
 * GNB 인기 타이틀 레일의 `See All Supported {PC,VR} Games`(시안 `30363:63982` PC ·
 * `30363:63984` VR)도 **여기로** 간다(`popular-titles-rail.tsx`). 그 링크가 경로를
 * 따로 적으면 아래 탭과 갈라지므로 한 곳에 둔다 — 이 파일이 이미 *"탭 정의를 복제하지
 * 않는다"* 를 규칙으로 두고 있어 같은 축이다.
 */
export const SUPPORTED_GAMES_HREF = {
  pc: "/games/pc/games",
  vr: "/games/vr/games",
} as const;

const PC_TABS = [
  { label: "Overview", href: "/games/pc" },
  { label: "Supported PC Games", href: SUPPORTED_GAMES_HREF.pc },
  { label: "Software", href: "/games/pc/software" },
] as const;

const VR_TABS = [
  { label: "Overview", href: "/games/vr" },
  { label: "Supported VR Games", href: SUPPORTED_GAMES_HREF.vr },
  // ⚠️ PC 가 `/games/pc/software` 라 VR 도 대칭일 것 같지만 **`vrplayer`** 다
  // (`src/pages/games/vr/software.tsx`). 푸터 v2 도 같은 경로를 쓴다 — 세 곳이
  // 갈라지지 않게 맞춘다.
  { label: "Software", href: "/games/vr/software" },
] as const;

/**
 * 경로 → 탭 목록. `startsWith` 로 본다 — `/games/vr` 와 `/games/vr/games` 가 같은 묶음이다.
 *
 * ⚠️ 기본값이 PC 다. VR 경로가 아니면 PC 를 그리므로, **새 플랫폼 탭을 추가할 때 여기
 * 분기를 같이 넣지 않으면 그 페이지에 PC 탭이 뜬다.**
 * `__tests__/v2-components/games-subnav-routes.test.ts` 가 이 대응을 잠근다.
 */
export function getSubNavTabs(pathname: string) {
  return pathname.startsWith("/games/vr") ? VR_TABS : PC_TABS;
}

/**
 * 경로로 섹션을 못 가르는 화면용.
 *
 * 게임 상세는 `/games/experiences/{slug}` 라 `getSubNavTabs` 의 `startsWith` 가 안 먹는다
 * (기본값이 PC 라 VR 게임에도 PC 탭이 뜬다). 그래서 **게임 데이터로** 섹션을 정해 넘긴다.
 *
 * ⚠️ URL 은 그대로 둔다. 경로를 `/games/vr/games/{slug}` 로 옮기는 것은 prod sitemap
 * **377개**가 이동하는 일이라 301 · canonical · locale prefix(`docs/pitfalls.md` #24
 * — Pages Router 리다이렉트에 getLocalePrefix 누락)까지 딸린 별건이다.
 * 시안이 요구하는 것은 **VR/PC 구분 노출**이고 그건 탭으로 된다.
 */
export type Section = "vr" | "pc";

// 두 목록이 `as const` 라 리터럴 타입이 달라진다 — 헬퍼 반환 타입으로 묶는다.
const TABS_BY_SECTION: Record<Section, ReturnType<typeof getSubNavTabs>> = {
  vr: VR_TABS,
  pc: PC_TABS,
};

export default function GamesSubNav({
  section,
  right,
}: {
  section?: Section;
  /**
   * 탭 오른쪽 슬롯 — **도킹된 검색창**이 들어온다 (#5475 · 기획 #5363 §2
   * *"…스크롤하면 서브내비 우측으로 도킹됩니다"*).
   *
   * ⚠️ **데스크탑에서만** 이 줄에 들어간다. 실측(2026-09-02 dev)으로 탭 끝 이후 남는 폭이
   * 1920 에서 **1167px**, 360 에서 **55px** 이다 — 모바일은 360 짜리 검색창이 물리적으로
   * 안 들어간다. 그래서 모바일은 호출처가 **아래 줄**에 따로 그린다.
   *
   * 높이는 안 맞춰도 된다 — 이 줄이 `h-12`(48) 이고 검색창도 48 이라 같다.
   */
  right?: React.ReactNode;
}) {
  /**
   * ★**`gnb` 하나만 선언한다** — 종전 `["games", "gnb"]` 는 **한글이 안 나왔다**.
   *
   * `useTranslation(ns[])` 은 **첫 ns 를 기본**으로 쓰고 나머지로 자동 폴백하지 않는다.
   * 이 컴포넌트의 `t()` 는 `t(tab.label)` **하나뿐**이고 그 라벨 4개
   * (`Overview` · 가운데 · `Software`)는 **전부 `gnb.json`** 에 있다 — `games` 가
   * 앞에 있으면 못 찾고 i18next 가 키를 그대로 돌려줘 **영문**이 나간다.
   *
   * 실측(2026-09-07 dev `/ko/games/pc/games/`): 서브내비가 `Overview · PC Games ·
   * Software` 로 **셋 다 영문**이었다(기대: `개요 · … · 소프트웨어`).
   * 영어 화면에서는 키가 곧 영문이라 정상으로 보여 안 드러났다.
   */
  const { t } = useTrans(["gnb"]);
  const router = useRouter();
  // 상세(`/games/experiences/{slug}`)처럼 경로로 못 가르는 화면은 `section` 을 넘긴다.
  const TABS = section
    ? TABS_BY_SECTION[section]
    : getSubNavTabs(router.pathname);

  /**
   * ★**활성 탭이 잘려 있으면 보이는 데까지 민다** (`GameTabMenu` 와 같은 동작).
   *
   * 스크롤만 켜면 가운데 탭이 활성인데도 화면 밖에 있을 수 있다 — 어느 탭에 있는지
   * 모른 채 시작한다. **잘렸을 때만** 민다(`nearest`) — 항상 밀면 이미 보이는 상태에서도
   * 화면이 한 번 튄다.
   *
   * ⚠️ `block: "nearest"` 다. 기본값(`start`)이면 **세로로도** 스크롤해 페이지가 위로 튄다.
   */
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeHref = section ? `/games/${section}/games` : router.pathname;
  useEffect(() => {
    const box = scrollRef.current;
    const el = box?.querySelector<HTMLElement>("[data-subnav-active='true']");
    if (!box || !el) return;
    const left = el.offsetLeft;
    const right = left + el.clientWidth;
    const visibleLeft = box.scrollLeft;
    const visibleRight = visibleLeft + box.clientWidth;
    if (left < visibleLeft || right > visibleRight) {
      el.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeHref]);

  return (
    /**
     * ★**아래 구분선은 데스크탑에만 있다** (개정 시안, 2026-09-01 실측).
     *
     * ```
     *   데스크탑 `29889:102531`(PC) · `29889:100936`(VR)
     *       bg-white **border-[#dde1e6] border-b border-solid** px-228 items-center
     *   모바일   `29942:15503`(PC Overview) · `29942:16127`(PC Games)
     *       bg-white  ← 보더 선언 **없음**  px-4 items-start
     * ```
     *
     * 종전엔 `border-b` 가 무조건 붙어 모바일에도 1px 선이 있었다. 폭 하나로만 확인하면
     * 「있다」가 두 폭 모두의 사실로 읽힌다 — 두 시안을 각각 `get_design_context` 로
     * 열어야 갈린 것이 보인다(`get_metadata` 는 보더를 주지 않는다).
     *
     * ⚠️ **높이도 1px 따라 움직인다.** 보더가 빠지면 모바일 nav 가 49 → **48** 이 되는데
     * 그게 시안 값이다(`29942:16127` h=48). 데스크탑은 48 + 1 로 그대로 둔다.
     *
     * ⚠️ 색까지 `laptop:` 로 접두한다. `border-coolgray-20` 만 접두 없이 두면 폭 0 이라
     * 화면엔 안 보이지만, 나중에 누가 `border` 를 하나 더 붙였을 때 모바일에 색이 살아난다.
     */
    <nav className="flex w-full justify-center bg-white laptop:border-b laptop:border-coolgray-20">
      {/* 시안 `29889:102531`(PC) · `29889:100936`(VR) — 개정 시안에서 58 → **48**.
          둘 다 48 인 것을 확인하고 한 번에 바꿨다(#5436). 한쪽만 바꾸면 두 탭 높이가 갈린다.
          ⚠️ 바깥 구조(`justify-center` + `flex-1`)는 #5427 이 만든 것이다 — 높이만 바꾼다. */}
      {/**
       * ★**모바일은 가로 스크롤이다** (시안 `29942:17594` `subnav-v2 / mobile`).
       *
       * 시안은 360 프레임에 콘텐츠가 **382** 라 3번째 탭이 넘친다 — 넘치는 만큼 밀어 본다
       * (`/games/` 탭 `GameTabMenu` 와 같은 방식).
       *
       * ⚠️ 종전엔 스크롤이 없고 `flex-1 min-w-0` 라 **긴 라벨이 눌렸다.** 실측
       * (2026-09-07 dev 360px `/de/games/vr/games/`): `Unterstützte VR-Spiele` 가
       * **두 줄로 접혀** 48px 행을 넘쳤다. 영어(`Supported VR Games` 123px)는 마침
       * 딱 맞아서 안 드러났다 — 한 폭·한 로케일만 보면 못 잡는 종류다.
       *
       * 그래서 셋이 함께 간다: **`overflow-x-auto`**(밀 수 있게) + **`w-max`**(내용 폭
       * 그대로) + 탭의 **`shrink-0 whitespace-nowrap`**(눌리지도 접히지도 않게).
       * 하나만 넣으면 안 된다 — `w-max` 없이는 `flex-1` 이 계속 줄이고,
       * `whitespace-nowrap` 없이는 줄바꿈으로 도망간다.
       *
       * ⚠️ 데스크탑은 종전 그대로다(`laptop:` 로 되돌린다). 데스크탑 시안은 이번에
       * 확인하지 않았다 — 모바일만 바꾼다.
       */}
      <div
        ref={scrollRef}
        className="scrollbar-none mx-1 h-12 min-w-0 max-w-screen-max flex-1 overflow-x-auto laptop:mx-4 laptop:overflow-x-visible"
      >
        <div className="flex h-full w-max items-center gap-4 laptop:w-full laptop:gap-10">
          {TABS.map((tab) => {
            // `router.pathname` 은 라우트 패턴이라 쿼리·trailing slash 가 없다. 세 href 도
            // 이제 순수 경로라(#5414 에서 `PC Games` 를 `/games/search/?filter=pc` → 전용
            // 라우트로 옮겼다) 그대로 비교하면 된다.
            //
            // ⚠️ 쿼리가 붙은 href 를 다시 넣게 되면 이 비교가 조용히 어긋난다 — 그때는
            // 경로만 떼어 비교할 것.
            /**
             * 경로 비교가 기본이고, `section` 이 오면(=상세) **그 섹션의 목록 탭**을 켠다.
             * 시안 `30035:18960` 이 상세에서 「VR Games」를 활성으로 그린다.
             */
            const active = section
              ? tab.href === `/games/${section}/games`
              : router.pathname === tab.href;
            return (
              <Link
                key={tab.label}
                href={tab.href}
                data-subnav-active={active}
                /**
                 * 스크린리더에 현재 위치를 알린다 (#5563).
                 *
                 * 종전엔 `data-subnav-active` 뿐이라 색·굵기·밑줄 어느 것도 소리로
                 * 전달되지 않았다 — 어느 탭에 있는지 알 방법이 없었다.
                 * `data-subnav-active` 는 그대로 둔다(스크롤 위치 계산이 그걸 읽는다).
                 */
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative flex h-full shrink-0 items-center whitespace-nowrap px-3 text-base transition-colors laptop:px-0",
                  active
                    ? "text-coolgray-100"
                    : "text-coolgray-60 hover:text-coolgray-100",
                )}
              >
                {/**
                 * ★**굵기로 선택을 가른다 — 비활성 400 · 활성 600** (#5563 · 작성자 요청).
                 *
                 * 시안 컴포넌트 `nav/tab`(`29889:102963`) 설명은 *"text 14/18
                 * SemiBold(고정) … 호버·활성 시 font weight 변경 금지(레이아웃 시프트
                 * 방지)"* 라고 적혀 있었고 이 자리는 그대로 따르고 있었다. 요청으로
                 * **의도적으로 벗어난다** — 색 대비만으로는 셋 다 활성으로 읽힌다는
                 * 것이 이슈의 근거다. 값은 리포 선례(~~`TactsuitSubMenu.tsx:129`·`142`~~
                 * 의 `font-400` + 선택 시 `font-600`)를 그대로 가져왔다(그 파일은
                 * 렌더 경로가 없는 고아라 2026-10-01 에 지웠다).
                 *
                 * ## ★시안이 걱정한 레이아웃 시프트는 **그대로 막는다**
                 *
                 * 굵기가 변하면 글자 폭이 변해 옆 탭이 밀린다. 그래서 볼드 사본을
                 * `invisible`(=`visibility:hidden`, 자리는 차지)로 겹쳐 **칸 폭을 항상
                 * 600 기준으로 예약**한다. 두 사본을 같은 격자 칸에 포개므로 폭은
                 * `max(400, 600)` = 600 폭으로 고정된다.
                 *
                 * 실측(2026-09-08 dev 1440 · `/games/pc` ↔ `/games/pc/games`):
                 * 고치기 전후 모두 `x = 16 · 120 · 303` · `w = 64 · 143 · 61` 로 동일하다.
                 *
                 * ⚠️ 사본에 `aria-hidden` 이 없으면 스크린리더가 라벨을 **두 번** 읽는다.
                 */}
                <span className="grid">
                  <span
                    className={cn(
                      "col-start-1 row-start-1",
                      active ? "font-600" : "font-400",
                    )}
                  >
                    {t(tab.label)}
                  </span>
                  <span
                    aria-hidden
                    className="invisible col-start-1 row-start-1 font-600"
                  >
                    {t(tab.label)}
                  </span>
                </span>
                {active && (
                  <span className="absolute inset-x-3 bottom-0 h-0.5 bg-coolgray-100 laptop:inset-x-0" />
                )}
              </Link>
            );
          })}

          {/* `ml-auto` — 탭은 왼쪽에 붙어 있고 이 슬롯만 오른쪽 끝으로 간다.
            `laptop:block` — 위 프롭 주석의 실측(모바일 여유 55px)대로 모바일에서는 숨긴다. */}
          {right && <div className="ml-auto hidden laptop:block">{right}</div>}
        </div>
      </div>
    </nav>
  );
}
